<template>
  <default-layout>
    <template>
      <v-container>
        <v-row>
          <!-- EDIT USER -->
          <v-col cols="12" md="4">
            <div>
              <v-form
                v-model="valid"
                ref="userEditForm"
                id="userEditForm"
                @submit.prevent="updateUser"
              >
                <v-card :loading="loading">
                  <v-toolbar flat>
                    <v-toolbar-title>Edit User</v-toolbar-title>
                  </v-toolbar>

                  <v-card-text>
                    <v-text-field
                      v-model="formUser.firstName"
                      :rules="nameRules"
                      :counter="25"
                      label="First name"
                      required
                    ></v-text-field>

                    <v-text-field
                      v-model="formUser.lastName"
                      :rules="nameRules"
                      :counter="25"
                      label="Last name"
                      required
                    ></v-text-field>

                    <v-text-field
                      v-model="formUser.email"
                      :counter="50"
                      :rules="emailRules"
                      label="E-mail"
                      required
                    ></v-text-field>

                    <v-select
                      v-if="formUser.role"
                      v-model="formUser.role"
                      :items="roleOptions"
                      item-text="name"
                      item-value="id"
                      label="Role"
                    ></v-select>

                    <v-select
                      v-if="formUser.facilities"
                      v-model="formUser.facilities"
                      :items="facilityOptions"
                      item-text="facilityId"
                      item-value="id"
                      label="Listing Access"
                      multiple
                      chips
                      clearable
                      deletable-chips
                      hint="What listings can this user access?"
                      persistent-hint
                    >
                      <template v-slot:prepend-item>
                        <v-list-item ripple @click="selectAllToggle">
                          <v-list-item-action>
                            <v-icon
                              :color="
                                formUser.facilities.length > 0 ? 'error' : ''
                              "
                              >{{ icon }}</v-icon
                            >
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title>{{
                              formUser.facilities.length === 0
                                ? "Select All"
                                : "Select None"
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider class="mt-2"></v-divider>
                      </template>

                      <template v-slot:item="{ item }">
                        {{ item.name }} &mdash; {{ item.facilityId }}
                      </template>
                    </v-select>
                  </v-card-text>

                  <v-card-actions>
                    <v-spacer />
                    <v-btn type="submit" color="primary"> Save </v-btn>
                  </v-card-actions>
                </v-card>
              </v-form>

              <v-snackbar v-model="showSnack" top :timeout="0" color="info">
                {{ snackMsg }}
                <v-btn text @click="showSnack = false"> Close </v-btn>
              </v-snackbar>
            </div>
          </v-col>

          <!-- RESET PASSWORD -->
          <v-col cols="12" md="4">
            <div>
              <v-form
                @submit.prevent="resetPassword"
                ref="resetForm"
                id="loginForm"
              >
                <v-card class="elevation-1" :loading="loading">
                  <v-toolbar flat>
                    <v-toolbar-title v-if="user"
                      >Change password</v-toolbar-title
                    >
                    <v-spacer />
                  </v-toolbar>
                  <v-card-text>
                    <v-text-field
                      id="password"
                      label="Password"
                      name="password"
                      prepend-icon="mdi-lock-reset"
                      type="password"
                      v-model="formUser.password"
                      :rules="passwordRules"
                    />

                    <v-text-field
                      id="confirm-password"
                      label="Confirm Password"
                      name="confirm-password"
                      prepend-icon="mdi-lock-reset"
                      type="password"
                      v-model="formUser.confirmPassword"
                      :rules="passwordRules"
                    />

                    <p class="error--text text-right">{{ error }}</p>
                  </v-card-text>

                  <v-card-actions>
                    <v-spacer />
                    <v-btn type="submit" color="primary">Reset</v-btn>
                  </v-card-actions>
                </v-card>
              </v-form>
              <v-snackbar v-model="showSnack" top :timeout="0" color="info">
                {{ snackMsg }}
                <v-btn text @click="showSnack = false"> Close </v-btn>
              </v-snackbar>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </template>
  </default-layout>
</template>

<script>
import { getUser, updateUser } from "../../api/user";
import { getCompanyFacilities } from "../../api/company";

export default {
  name: "UserEditForm",
  title: "Edit User",

  data() {
    return {
      valid: false,
      user: null,
      formUser: {
        firstName: "",
        lastName: "",
        email: "",
        companyId: "",
        role: "",
        facilities: [],
        password: "",
        confirmPassword: ""
      },
      loading: false,
      showSnack: false,
      snackMsg: "",
      error: null,
      roleOptions: [
        {
          id: 1,
          name: "Admin",
          slug: "admin"
        },
        {
          id: 2,
          name: "Admissions",
          slug: "admissions"
        }
      ],
      facilityOptions: [],

      nameRules: [
        v => !!v || "Name is required",
        v => v.length <= 25 || "Name must be less than 25 characters"
      ],
      emailRules: [
        v => !!v || "E-mail is required",
        v => /.+@.+/.test(v) || "E-mail must be valid",
        v => v.length <= 50 || "Name must be less than 25 characters"
      ],
      passwordRules: [
        v => !!v || "New password is required",
        v => v.length >= 8 || "Must be at least 8 characters long"
      ]
    };
  },

  computed: {
    icon() {
      if (this.formUser.facilities.length > 0) return "mdi-close-box";
      return "mdi-checkbox-blank-outline";
    }
  },

  async mounted() {
    await this.getUser();
    await this.getFacilityOptions();
  },

  methods: {
    async getUser() {
      const userId = parseInt(this.$route.params.userId);
      if (userId) {
        this.loading = true;

        const result = await getUser(userId);
        this.user = result;
        this.formUser.firstName = this.user.firstName;
        this.formUser.lastName = this.user.lastName;
        this.formUser.email = this.user.email;
        this.formUser.companyId = this.user.companyId;
        this.formUser.facilities = this.user.facilities;
        this.formUser.role = this.roleOptions.filter(
          option => option.name == this.user.role
        )[0].id;
        this.$title = `Edit ${this.user.firstName} ${this.user.lastName}`;

        this.loading = false;
      }
    },
    async getFacilityOptions() {
      if (this.user && this.user.companyId) {
        this.loading = true;

        const result = await getCompanyFacilities(this.user.companyId);
        this.facilityOptions = result.facilities.map(facility => {
          return {
            id: facility.connectId,
            name: facility.name,
            facilityId: facility.externalId
          };
        });

        this.loading = false;
      }
    },
    async updateUser() {
      this.loading = true;
      const valid = this.$refs.userEditForm.validate();
      if (!valid) {
        this.loading = false;
        return;
      }

      try {
        await updateUser(this.user.id, {
          firstName: this.formUser.firstName,
          lastName: this.formUser.lastName,
          email: this.formUser.email,
          companyId: this.formUser.companyId,
          role: this.formUser.role,
          facilities: this.formUser.facilities
        });

        this.snackMsg = "Updated user successfully.";
        this.showSnack = true;

        this.loading = false;
      } catch (e) {
        this.loading = false;
      }
    },
    async resetPassword() {
      const userId = parseInt(this.$route.params.userId);
      if (!userId) {
        return;
      }

      this.error = null;
      this.loading = true;
      const valid = this.$refs.resetForm.validate();
      if (!valid) {
        this.loading = false;
        return;
      } else if (this.formUser.password !== this.formUser.confirmPassword) {
        this.loading = false;
        this.error = "Passwords don't match.";
        return;
      }

      try {
        await updateUser(userId, { password: this.formUser.password });
        this.loading = false;
        this.snackMsg =
          "Password updated successfully. Please distribute the new password to the user.";
        this.showSnack = true;
      } catch (e) {
        this.loading = false;
        console.error(e);
        this.error = e;
        throw e;
      }
    },
    selectAllToggle() {
      if (this.formUser.facilities.length > 0) {
        return (this.formUser.facilities = []);
      }
      this.formUser.facilities = this.facilityOptions.map(
        facility => facility.id
      );
    }
  }
};
</script>
